import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { Card } from 'antd';
import { createNote, getActivity, getNotes } from '../../services/detailedViewApi';
import DynamicTabs from '../DynamicTabs';
import ActivityTab from './ActivityTab';
import NotesTab from './NotesTab';
import '../../styles/activity-notes.css';

/*
 * Self-contained "Activity & Notes" card used on the right rail of a detail
 * view. Holds two tabs — Activity (read-only timeline) and Notes (composer +
 * timeline).
 *
 * Given `moduleName` + `id` it fetches its own Activity & Notes and posts new
 * notes back to the API. DetailViewPage just passes moduleName + id; when those
 * are absent each tab falls back to its own sample data so the card still
 * renders standalone.
 */
export default function ActivityNotesCard({
  moduleName,
  id,
  users = [],
  showActivity = true,
  showNotes = true,
  allowAddNote = true,
  defaultActiveKey = 'activity',
  refreshKey = 0,
  className = '',
}) {
  const [activity, setActivity] = useState(null);
  const [notes, setNotes] = useState(null);

  // Load the Activity & Notes for the current record.
  useEffect(() => {
    if (!moduleName || !id) return undefined;

    let cancelled = false;

    async function fetchActivityAndNotes() {
      const [activityData, notesData] = await Promise.all([
        showActivity ? getActivity(moduleName, id).catch(() => []) : Promise.resolve(null),
        showNotes ? getNotes(id).catch(() => []) : Promise.resolve(null),
      ]);
      if (!cancelled) {
        setActivity(activityData);
        setNotes(notesData);
      }
    }

    fetchActivityAndNotes();

    return () => {
      cancelled = true;
    };
  }, [moduleName, id, showActivity, showNotes, refreshKey]);

  const handleAddNote = useCallback(
    async (text) => {
      if (!id) return;
      // Pass the module so the backend tags the note and logs the activity.
      await createNote({ relatedId: id, notes: text, notesFor: moduleName });
      // A new note also creates an activity entry, so refresh both timelines.
      const [notesData, activityData] = await Promise.all([
        getNotes(id).catch(() => []),
        showActivity ? getActivity(moduleName, id).catch(() => []) : Promise.resolve(activity),
      ]);
      setNotes(notesData);
      if (showActivity) setActivity(activityData);
    },
    [id, moduleName, showActivity, activity],
  );

  // Only render the tabs that are enabled for this page.
  const tabs = [
    showActivity && {
      key: 'activity',
      title: 'Activity',
      count: Array.isArray(activity) ? activity.length : undefined,
      children: <ActivityTab activity={activity} />,
    },
    showNotes && {
      key: 'notes',
      title: 'Notes',
      count: Array.isArray(notes) ? notes.length : undefined,
      children: (
        <NotesTab
          notes={notes}
          users={users}
          allowAddNote={allowAddNote}
          onAddNote={id ? handleAddNote : undefined}
        />
      ),
    },
  ].filter(Boolean);

  // Fall back to the first available tab if the default one is hidden.
  const initialKey = tabs.some((tab) => tab.key === defaultActiveKey)
    ? defaultActiveKey
    : tabs[0]?.key;
  const [activeKey, setActiveKey] = useState(initialKey);

  return (
    <Card className={['znx-an-card', className].filter(Boolean).join(' ')} styles={{ body: { padding: 0 } }}>
      <DynamicTabs
        className="znx-an-tabs"
        tabs={tabs}
        activeKey={activeKey}
        onChange={setActiveKey}
      />
    </Card>
  );
}

ActivityNotesCard.propTypes = {
  moduleName: PropTypes.string,
  id: PropTypes.string,
  users: PropTypes.array,
  showActivity: PropTypes.bool,
  showNotes: PropTypes.bool,
  // Set false to render the Notes tab read-only: the timeline stays, the
  // composer goes. For hosts that author notes elsewhere (Timesheet keeps its
  // own Notes / Comments field, on a different store) — the tab would
  // otherwise be a second, unconnected place to write about the same record.
  allowAddNote: PropTypes.bool,
  defaultActiveKey: PropTypes.string,
  refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Extra class(es) on the outer Card — e.g. a host page's own sizing hooks
  // (flex-fill sidebar, height caps) layered on top of the base znx-an-card
  // look, without the host having to reimplement the card shell.
  className: PropTypes.string,
};
